import { Injectable } from '@angular/core';
import { AsyncSubject, Observable, from } from 'rxjs';
import { delay, filter, finalize, map, mergeMap } from 'rxjs/operators';

import {
  Checklist,
  ChecklistCommitParams,
  ChecklistCondition,
  ChecklistConditionsCreateParams,
  ChecklistCreateParams,
  ChecklistItem,
  ChecklistItemsCreateParams,
  ChecklistLink,
  ChecklistLinkQueryParams,
  ChecklistSaveParams,
  Checklists,
} from '../models/checklist.model';
import { NeverError } from '../models/error.model';
import { DistinctSubject, recursiveQuery } from '../models/utility.model';
import { WebSocketSyncData } from '../models/web-socket.model';
import { ChecklistGateway } from '../usecases/checklist.gateway';
import { ChecklistUsecase } from '../usecases/checklist.usecase';
import { CloudStorageGateway } from '../usecases/cloud-storage.gateway';
import { ProgressUsecase } from '../usecases/progress.usecase';
import { WebSocketUsecase } from '../usecases/web-socket.usecase';

const UPLOAD_DELAY = 10 * 1000;

@Injectable()
export class ChecklistInteractor extends ChecklistUsecase {
  get checklists$(): Observable<Checklists> {
    return this._checklists.pipe(
      map(checklists =>
        checklists.values().map(checklist => ({
          ...checklist,
          checklistLinks$: this.listChecklistLinks(checklist.checklistId, checklist.organizationId),
          checklistFlowchart$: this.getChecklistFlowchart(checklist.checklistId),
          checklistItems$: this.listChecklistItems(checklist.checklistId),
          checklistConditions$: this.listChecklistConditions(checklist.checklistId),
        })),
      ),
      map(checklists => new Checklists(checklists)),
    );
  }

  private readonly _checklists = new DistinctSubject<Checklists>(new Checklists());

  constructor(
    private _progressUsecase: ProgressUsecase,
    private _webSocketUsecase: WebSocketUsecase,
    private _checklistGateway: ChecklistGateway,
    private _cloudStorageGateway: CloudStorageGateway,
  ) {
    super();
    this._webSocketUsecase.isOpen$.subscribe(isOpen => (isOpen ? this.onSignIn() : this.onSignOut()));
    this._webSocketUsecase.message$
      .pipe(
        filter(message => message.action === 'sync' && message.data?.source === 'checklist'),
        map(({ data }) => data as WebSocketSyncData<Checklist>),
      )
      .subscribe(data => {
        switch (data.reason) {
          case 'create':
          case 'update':
            this._checklists.next(this._checklists.value.set(data.payload));
            break;
          case 'delete':
            this._checklists.next(this._checklists.value.delete((data.payload as Checklist).checklistId));
            break;
          default:
            throw new NeverError(data.reason);
        }
      });
  }

  createChecklist(params: ChecklistCreateParams): Observable<string> {
    const result = new AsyncSubject<string>();
    this._checklistGateway.createChecklist(params).subscribe({
      next: createdChecklist => {
        this._checklists.next(this._checklists.value.set(createdChecklist));
        result.next(createdChecklist.checklistId);
      },
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  saveChecklist(params: ChecklistSaveParams): Observable<string> {
    const result = new AsyncSubject<string>();
    this._checklistGateway.saveChecklist(params).subscribe({
      next: savedChecklist => {
        this._checklists.next(this._checklists.value.set(savedChecklist));
        result.next(savedChecklist.checklistId);
      },
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  commitChecklist(params: ChecklistCommitParams): Observable<string> {
    const result = new AsyncSubject<string>();
    this._checklistGateway.commitChecklist(params).subscribe({
      next: committedChecklist => {
        this._checklists.next(this._checklists.value.set(committedChecklist));
        result.next(committedChecklist.checklistId);
      },
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  deleteChecklist(checklistId: string): Observable<never> {
    const result = new AsyncSubject<never>();
    this._checklistGateway.deleteChecklist(checklistId).subscribe({
      next: () => this._checklists.next(this._checklists.value.delete(checklistId)),
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  createChecklistFlowchart(checklistId: string, flowchart: string): Observable<never> {
    const progressId = this._progressUsecase.show();
    const result = new AsyncSubject<never>();
    const blob = new Blob([flowchart], { type: 'text/plain; charset=utf-8' });
    this._checklistGateway
      .createChecklistFlowchart(checklistId)
      .pipe(
        mergeMap(storage => this._cloudStorageGateway.upload(storage, blob)),
        delay(UPLOAD_DELAY),
        finalize(() => this._progressUsecase.dismiss(progressId)),
      )
      .subscribe({
        error: result.error.bind(result),
        complete: result.complete.bind(result),
      });
    return result.asObservable();
  }

  createChecklistItems(checklistId: string, params: ChecklistItemsCreateParams): Observable<never> {
    const result = new AsyncSubject<never>();
    this._checklistGateway.createChecklistItems(checklistId, params).subscribe({
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  createChecklistConditions(checklistId: string, params: ChecklistConditionsCreateParams): Observable<never> {
    const result = new AsyncSubject<never>();
    this._checklistGateway.createChecklistConditions(checklistId, params).subscribe({
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  createHash(jsonData: Record<string, unknown>): Observable<string> {
    const jsonToString = this.stableStringify(jsonData);
    const encoder = new TextEncoder();
    const data = encoder.encode(jsonToString);

    return from(crypto.subtle.digest('SHA-256', data)).pipe(
      map(hashBuffer => Array.from(new Uint8Array(hashBuffer))),
      map(hashArray => hashArray.map(b => b.toString(16).padStart(2, '0')).join('')),
    );
  }

  private onSignIn(): void {
    recursiveQuery(params => this._checklistGateway.listChecklists(params), {}).subscribe(checklists => {
      this._checklists.next(new Checklists(checklists));
    });
  }

  private onSignOut(): void {
    this._checklists.next(new Checklists());
  }

  private listChecklistLinks(checklistId: string, organizationId: string): Observable<ChecklistLink[]> {
    const queryParams: ChecklistLinkQueryParams = { organizationId };
    return recursiveQuery(params => this._checklistGateway.listChecklistLinks(checklistId, params), queryParams, true);
  }

  private getChecklistFlowchart(checklistId: string): Observable<string> {
    return this._checklistGateway.getChecklistFlowchart(checklistId);
  }

  private listChecklistItems(checklistId: string): Observable<ChecklistItem[]> {
    return recursiveQuery(params => this._checklistGateway.listChecklistItems(checklistId, params), {}, true);
  }

  private listChecklistConditions(checklistId: string): Observable<ChecklistCondition[]> {
    return recursiveQuery(params => this._checklistGateway.listChecklistConditions(checklistId, params), {}, true);
  }

  private stableStringify(value: unknown): string {
    if (value === null || typeof value !== 'object') return JSON.stringify(value);
    if (Array.isArray(value)) {
      const elements = value.map(v => this.stableStringify(v));
      return `[${elements.join(',')}]`;
    }
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    const entries = keys.map(key => {
      const val = obj[key];
      return `"${key}":${this.stableStringify(val)}`;
    });
    return `{${entries.join(',')}}`;
  }
}
